import { useState, useRef } from "react";
import { X, Upload, Camera, FolderOpen, Loader2 } from "lucide-react";
import type { Receipt, Category } from "../../types";
import { useIsMobile } from "../../hooks/useIsMobile";

interface Props {
  onUpload: (file: File) => Promise<Receipt>;
  onSave: (id: string, updates: Partial<Receipt>) => Promise<Receipt>;
  onClose: () => void;
  categories: Category[];
}

type Step = "pick" | "processing" | "review";

export function UploadModal({ onUpload, onSave, onClose, categories }: Props) {
  const [step, setStep] = useState<Step>("pick");
  const [draft, setDraft] = useState<Receipt | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  async function handleFile(file: File) {
    setStep("processing");
    setError("");
    try {
      const receipt = await onUpload(file);
      setDraft(receipt);
      setStep("review");
    } catch {
      setError("Upload failed. Please try again.");
      setStep("pick");
    }
  }

  async function handleSave() {
    if (!draft) return;
    setSaving(true);
    try {
      await onSave(draft.id, {
        merchant: draft.merchant ?? undefined,
        receipt_date: draft.receipt_date ?? undefined,
        amount: draft.amount ?? undefined,
        vat_amount: draft.vat_amount ?? undefined,
        category_id: draft.category_id ?? undefined,
        notes: draft.notes ?? undefined,
      });
      onClose();
    } catch {
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const overlayStyle: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 100,
    padding: isMobile ? "1rem" : 0,
  };
  const modalStyle: React.CSSProperties = {
    background: "#1e293b", borderRadius: "8px", padding: "1.5rem",
    width: "100%", maxWidth: "480px", maxHeight: "90vh", overflowY: "auto",
    position: "relative",
  };
  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "0.5rem 0.75rem", background: "#0f172a",
    border: "1px solid #334155", borderRadius: "4px", color: "#f1f5f9", boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = { color: "#94a3b8", fontSize: "0.8rem", marginBottom: "0.25rem", display: "block" };

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <button onClick={onClose} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "#64748b", cursor: "pointer", display: "flex" }}><X size={20} /></button>
        <h2 style={{ color: "#f1f5f9", marginBottom: "1.25rem" }}>New Receipt</h2>

        {step === "pick" && (
          <>
            {isMobile ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1rem" }}>
                <button
                  onClick={() => cameraRef.current?.click()}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", padding: "1.25rem", background: "#0f172a", border: "2px solid #3b82f6", borderRadius: "8px", color: "#3b82f6", fontSize: "1rem", fontWeight: 600, cursor: "pointer" }}
                >
                  <Camera size={24} /> Take photo
                </button>
                <button
                  onClick={() => inputRef.current?.click()}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", padding: "1.25rem", background: "#0f172a", border: "2px solid #334155", borderRadius: "8px", color: "#94a3b8", fontSize: "1rem", fontWeight: 600, cursor: "pointer" }}
                >
                  <FolderOpen size={24} /> Choose from gallery / files
                </button>
              </div>
            ) : (
              <div
                onClick={() => inputRef.current?.click()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
                onDragOver={e => e.preventDefault()}
                style={{ border: "2px dashed #3b82f6", borderRadius: "6px", padding: "2rem", textAlign: "center", cursor: "pointer", marginBottom: "1rem" }}
              >
                <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.75rem", color: "#3b82f6" }}><Upload size={36} strokeWidth={1.5} /></div>
                <div style={{ color: "#3b82f6", fontWeight: 500 }}>Click to choose or drop a file</div>
                <div style={{ color: "#475569", fontSize: "0.8rem", marginTop: "0.25rem" }}>JPG, PNG, PDF accepted</div>
              </div>
            )}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment"
              style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
              style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            {error && <p style={{ color: "#f87171", fontSize: "0.875rem" }}>{error}</p>}
          </>
        )}

        {step === "processing" && (
          <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}><Loader2 size={36} className="spin" /></div>
            Reading receipt…
          </div>
        )}

        {step === "review" && draft && (
          <div>
            {draft.ocr_confidence !== null && draft.ocr_confidence < 0.7 && (
              <div style={{ background: "#78350f33", border: "1px solid #d9770655", borderRadius: "4px", padding: "0.5rem 0.75rem", color: "#fbbf24", fontSize: "0.8rem", marginBottom: "1rem" }}>
                Low OCR confidence — please review the extracted fields carefully.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <div>
                <label style={labelStyle}>MERCHANT</label>
                <input style={inputStyle} value={draft.merchant ?? ""} onChange={e => setDraft({ ...draft, merchant: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>DATE</label>
                <input style={inputStyle} type="date" value={draft.receipt_date ?? ""} onChange={e => setDraft({ ...draft, receipt_date: e.target.value })} />
              </div>
              <div>
                <label style={{ ...labelStyle, color: draft.ocr_confidence !== null && draft.ocr_confidence < 0.7 ? "#fbbf24" : "#94a3b8" }}>AMOUNT (£)</label>
                <input style={inputStyle} type="number" step="0.01" value={draft.amount ?? ""} onChange={e => setDraft({ ...draft, amount: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>VAT (£)</label>
                <input style={inputStyle} type="number" step="0.01" value={draft.vat_amount ?? ""} onChange={e => setDraft({ ...draft, vat_amount: e.target.value })} />
              </div>
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={labelStyle}>CATEGORY</label>
              <select style={{ ...inputStyle, fontSize: "1rem" }} value={draft.category_id ?? ""} onChange={e => setDraft({ ...draft, category_id: e.target.value || null })}>
                <option value="">Uncategorised</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={labelStyle}>NOTES</label>
              <textarea style={{ ...inputStyle, height: "60px", resize: "vertical" }} value={draft.notes ?? ""} onChange={e => setDraft({ ...draft, notes: e.target.value })} />
            </div>
            {error && <p style={{ color: "#f87171", fontSize: "0.875rem", marginBottom: "0.75rem" }}>{error}</p>}
            <button onClick={handleSave} disabled={saving}
              style={{ width: "100%", padding: "0.625rem", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: "4px", fontSize: "1rem", cursor: "pointer" }}>
              {saving ? "Saving…" : "Save Receipt"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
